import { useEffect, useState } from "react";

import { DataTableConfig, DEFAULT_BATCH_CAPACITY, BatchCapacity, Header } from "@/Shared/Types/datatable.types";
import { RecordHelper } from "@/Shared/Helpers/record.helper";
import { DateFormatHelper, SearchHelper } from "@/Shared/Helpers";
import { SoNumber } from "@/Shared/Helpers/so-number";
import { EXPORT_FORMATS, EXPORT_TYPES, ExportFilter, ExportParams, CASES, OPERATORS, SearchOrder } from "@/Shared/Types";


/**
 * Author: Samano CASTRE
 * Date: 2025-06-29
 * 
 * This custom React hook manages a data table with features including:
 * - Sorting and filtering with optional case sensitivity
 * - Data batching (pagination) for incremental loading
 * - Handling row clicks and dynamic batch capacity
 * 
 * It returns the processed (filtered/sorted) and paged data slice along with
 * utilities to control sorting, filtering, and loading more data.
 */
export const useDataTable = <VM extends Record<string, any>>(dataConfig: DataTableConfig<VM>
) => {
    const {
        domainLabel,
        headers,
        initialData,
        noStickyHeader = false,
        initialBatchCapacity = DEFAULT_BATCH_CAPACITY,
        defaultCaseSensitive = false,
        Icon,
        userActions,
        isRestorable,
        newItems,
        title,
        headerAction
    } = dataConfig;

    const [batchIndex, setBatchIndex] = useState(0);
    const [batchCapacity, setBatchCapacity] = useState<BatchCapacity>(initialBatchCapacity);
    const [slicedData, setSlicedData] = useState<VM[]>([]);
    const [processedData, setProcessedData] = useState<VM[]>(initialData);
    const [activeFilters, setActiveFilters] = useState<Record<keyof VM, ExportFilter>>();

    const [sortColumn, setSortColumn] = useState<keyof VM>();
    const [sortOrder, setSortOrder] = useState<SearchOrder>();

    useEffect(() => {
        //console.table(initialData);
        setProcessedData(initialData);
        setBatchIndex(0); // Recommencer le paging depuis le début
    }, [initialData]);

    /**
     * Effect to handle batching of data:
     * Calculates the current slice of data to display based on batchIndex and batchCapacity,
     * and dispatches the total number of items.
     */
    useEffect(() => {
        const start = batchIndex * batchCapacity;
        const end = start + batchCapacity;
        const nextBatch = processedData.slice(start, end);

        setSlicedData(prev => batchIndex === 0 ? nextBatch : [...prev, ...nextBatch]);
    }, [batchIndex, processedData, batchCapacity]);

    const handleFilterChange = (currentHeader: Header<VM>, currentValue: any) => {
        const noValue = currentValue === null || currentValue === undefined || currentValue.length === 0;

        const activeFilter = activeFilters?.[currentHeader.key];

        if (noValue && !activeFilter) return; // Do nothing

        const allActiveFilters = { ...activeFilters } as Record<keyof VM, any>;

        if (noValue && activeFilter) {
            delete allActiveFilters[currentHeader.key];
        }
        else {
            const operator = currentHeader.filter?.operator ?? (currentHeader.filter?.options ? OPERATORS.EQUALS : OPERATORS.CONTAINS);
            const filterCase = currentHeader.filter?.case || defaultCaseSensitive || CASES.INSENSITIVE;

            allActiveFilters[currentHeader.key] = {
                value: currentValue,
                operator,
                case: filterCase,
                label: currentHeader.filter?.options?.find(opt => opt.value === currentValue)?.label
            } as Record<keyof VM, ExportFilter>;
        }

        setActiveFilters(allActiveFilters);

        if (RecordHelper.isEmpty(allActiveFilters)) {
            if (JSON.stringify(initialData) !== JSON.stringify(processedData)) {
                setProcessedData(initialData);
                setBatchIndex(0);
            }
            return;
        }
        processFilter(allActiveFilters);
    }


    /**
     * Filters initialData based on the header configuration and search value.
     * Supports case sensitivity and different filter input types ('input' or 'select').
     */
    const processFilter = (allActiveFilters: Record<keyof VM, ExportFilter>) => {

        const mapData = new Map<number, number>();

        initialData.forEach((row, index) => {

            RecordHelper.toArray(allActiveFilters).forEach(([key, value]) => {

                const search = value as ExportFilter;
                const searchCase = search.case;
                const searchValue = search.label || search.value;
                const searchOperator = search.operator;

                const header = headers[key as keyof VM];

                let column = row[header.key];

                if (!column) return;


                const isEqualIgnoreCase = () => String(column).toUpperCase() === searchValue.toUpperCase();

                const isEqual = () => String(column) === searchValue;

                //const isCaseSensitive = () => header.caseSensitive?.filter || defaultCaseSensitive;

                const isInclude = () => String(column).includes(searchValue);

                const isIncludeIgnoreCase = () => String(column).toUpperCase().includes(searchValue.toUpperCase())

                const incrementIndex = () => mapData.set(index, (mapData.get(index) || 0) + 1);

                if (SearchHelper.isInSensitive(searchCase)
                    && SearchHelper.isEqualsSearch(searchOperator)
                    && isEqualIgnoreCase()) {

                    incrementIndex();
                }

                if (SearchHelper.isSensitive(searchCase)
                    && SearchHelper.isEqualsSearch(searchOperator)
                    && isEqual()) {
                    incrementIndex();
                }

                if (SearchHelper.isInSensitive(searchCase)
                    && SearchHelper.isContainsSearch(searchOperator)
                    && isIncludeIgnoreCase()) {
                    incrementIndex();
                }

                if (SearchHelper.isSensitive(searchCase)
                    && SearchHelper.isContainsSearch(searchOperator)
                    && isInclude()) {
                    incrementIndex();
                }
            });
        });

        const filteredData = initialData.filter((data, index) => mapData.get(index) === RecordHelper.size(allActiveFilters));


        if (JSON.stringify(filteredData) !== JSON.stringify(processedData)) {
            setProcessedData(filteredData);
            setBatchIndex(0);
        }
        if (sortColumn && sortOrder) {
            processSort(sortColumn, sortOrder);
        }
    }

    /**
    * Sorts initialData based on the header key and specified order.
    * Supports sorting of dates, numbers, and strings with optional case sensitivity.
    */
    const processSort = (key: keyof VM, order: SearchOrder) => {

        const header = headers[key];
        if (!header) return;

        const compareStr = (str1: string, str2: string) => {
            return SearchHelper.isASC(order) ? str1.localeCompare(str2) : str2.localeCompare(str1);
        }
        const compareNumber = (num1: number, num2: number) => {
            if (num1 == null || num2 == null) return 0;
            return SearchHelper.isASC(order) ? num1 - num2 : num2 - num1;
        }

        const sortedData = [...processedData].sort((row1, row2) => {
            const val1 = row1[header.key];
            const val2 = row2[header.key];
            const type = header.type.toLowerCase();

            if (type === 'date') {
                return compareNumber(
                    DateFormatHelper.fromFrenchDate(String(val1)),
                    DateFormatHelper.fromFrenchDate(String(val2))
                );
            }

            if (type === 'number') {
                const num1 = Number(SoNumber.unGroup(val1 as string));
                const num2 = Number(SoNumber.unGroup(val2 as string));
                return compareNumber(
                    isNaN(num1) ? 0 : num1,
                    isNaN(num2) ? 0 : num2
                );
            }

            return compareStr(
                String(val1 ?? "").toUpperCase(),
                String(val2 ?? "").toUpperCase()
            );
        });

        if (JSON.stringify(sortedData) !== JSON.stringify(processedData)) {
            setProcessedData(sortedData);
            setBatchIndex(0);
        }
        setSortColumn(key);
        setSortOrder(order);
    };

    /**
     * Returns true if at least one header is filterable.
     */
    const isFilterable = (Object.values(headers) as Header<VM>[]).some(header => !!header.filter);


    /**
     * Returns true if there is more data to load beyond the current slice.
     */
    const hasMoreData = () => slicedData.length < processedData.length;


    /**
     * Loads the next batch of data if available.
     */
    const loadMoreData = () => {
        if (!hasMoreData()) return;
        setBatchIndex(prev => prev + 1);
    }

    const updateBatchCapacity = (newCapacity: BatchCapacity) => {
        setBatchIndex(0);
        setBatchCapacity(newCapacity);

    }

    const resetFilter = () => {
        setActiveFilters(undefined);
        setSortColumn(undefined);
        setProcessedData(initialData);
    }

    const handleSortChange = (value: string) => {
        if (!value) return;
        const tabSort = value.split('-');
        const key = tabSort[0] as keyof VM;
        const order = tabSort[1] as SearchOrder;
        processSort(key, order);
    }

    const handleExcelExport = () => {
        const params = {
            filters: activeFilters,
            sorting: [{
                column: sortColumn,
                order: sortOrder
            }]
        } as ExportParams<VM>;

        userActions?.crud?.generate?.(params, EXPORT_FORMATS.EXCEL);
    }


    return {
        domainLabel,
        headers,
        slicedData,
        noStickyHeader,
        hasMoreData,
        isFilterable,
        batchCapacity,
        handleFilterChange,
        processSort,
        updateBatchCapacity,
        loadMoreData,
        processedDataCount: processedData.length,
        resetFilter,

        isReset: RecordHelper.isEmpty(activeFilters),
        activeFiltersCount: RecordHelper.size(activeFilters),
        activeFilters,
        Icon,
        sort: {
            column: sortColumn,
            order: sortOrder
        },
        handleSortChange,
        domainMode: userActions?.domainMode,
        isRestorable,
        newItems,
        title,
        headerAction,
        actionHandlers: {
            handleExcelExport: userActions?.crud?.generate ? handleExcelExport : undefined,
            handleCreate: userActions?.crud?.create,
            handleRead: userActions?.crud?.read,
            handleEdit: userActions?.crud?.edit,
            handleDelete: userActions?.crud?.delete,
            handleRestore: userActions?.crud?.restore
        },
    };
}